import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRocket, 
  faUndo,
  faCheck,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';
import { OtaMode } from '../utils/otaMode';

interface DeploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiBaseUrl: string;
  onDeploymentComplete: () => void;
  alphabet: string[];
  selectedGroups: {[key: string]: boolean};
  setSelectedGroups: React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>;
  inline?: boolean;
}

const DeploymentModal: React.FC<DeploymentModalProps> = ({ 
  isOpen, 
  onClose, 
  apiBaseUrl, 
  onDeploymentComplete,
  alphabet,
  selectedGroups,
  setSelectedGroups,
  inline = false
}) => {
  const [processingAction, setProcessingAction] = useState<boolean>(false);
  const [versions, setVersions] = useState<string[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<{[key: string]: boolean}>({});
  const [versionFilterMode, setVersionFilterMode] = useState<'include' | 'exclude'>('include');
  const [loading, setLoading] = useState<boolean>(true);
  const [useVersionFilter, setUseVersionFilter] = useState<boolean>(false);

  // Check if any groups are selected
  const hasSelectedGroups = Object.values(selectedGroups).some(isSelected => isSelected);

  // Fetch available versions when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchVersions();
    }
  }, [isOpen, apiBaseUrl]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/devices/versions`, {
        withCredentials: true
      });
      setVersions(response.data);
      
      // Initialize version selection state
      const initialVersionSelections: {[key: string]: boolean} = {};
      response.data.forEach((version: string) => {
        initialVersionSelections[version] = false;
      });
      setSelectedVersions(initialVersionSelections);
      
    } catch (error) {
      console.error('Error fetching device versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (group: string) => {
    setSelectedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const toggleVersion = (version: string) => {
    setSelectedVersions(prev => ({
      ...prev,
      [version]: !prev[version]
    }));
  };

  const selectAllGroups = () => {
    const updatedGroups = {...selectedGroups};
    Object.keys(updatedGroups).forEach(key => {
      updatedGroups[key] = true;
    });
    setSelectedGroups(updatedGroups);
  };

  const clearAllGroups = () => {
    const updatedGroups = {...selectedGroups};
    Object.keys(updatedGroups).forEach(key => {
      updatedGroups[key] = false;
    });
    setSelectedGroups(updatedGroups);
  };

  const selectAllVersions = () => {
    const updatedVersions = {...selectedVersions};
    Object.keys(updatedVersions).forEach(key => {
      updatedVersions[key] = true;
    });
    setSelectedVersions(updatedVersions);
  };

  const clearAllVersions = () => {
    const updatedVersions = {...selectedVersions};
    Object.keys(updatedVersions).forEach(key => {
      updatedVersions[key] = false;
    });
    setSelectedVersions(updatedVersions);
  };

  const handleDeployment = async (otaMode: OtaMode) => {
    // Get the selected groups
    const groups = Object.entries(selectedGroups)
      .filter(([_, selected]) => selected)
      .map(([group]) => group);
    
    if (groups.length === 0) {
      alert("Please select at least one group");
      return;
    }

    // Get the selected versions if using version filter
    const versionsList = useVersionFilter 
      ? Object.entries(selectedVersions)
          .filter(([_, selected]) => selected)
          .map(([version]) => version)
      : [];
    
    if (useVersionFilter && versionsList.length === 0) {
      alert("Please select at least one version or disable version filtering");
      return;
    }

    setProcessingAction(true);
    try {
      const response = await axios.post(
        `${apiBaseUrl}/devices/update-by-groups-and-versions`,
        { 
          groups,
          type: "prod", // Target only production devices
          versions: useVersionFilter ? versionsList : null,
          versionFilterMode, // include or exclude
          otaMode,
        },
        { withCredentials: true }
      );
      
      // Show success message
      const action = otaMode === 'none' ? 'cleared' : `set to ${otaMode}`;
      const versionInfo = useVersionFilter 
        ? `with version filter: ${versionFilterMode === 'include' ? 'only' : 'excluding'} ${versionsList.join(', ')}`
        : 'across all versions';
        
      console.log(`Successfully ${action} OTA for ${response.data.modifiedCount} devices in groups: ${groups.join(', ')} ${versionInfo}`);
      
      // Notify parent component about the update
      onDeploymentComplete();
      
      // Close the modal after successful operation
      if (otaMode !== 'none') {
        onClose();
      }

    } catch (error: any) {
      console.error('Error updating devices by groups and versions:', error);
      alert(`Failed to update OTA mode: ${error.response?.data?.message || error.message}`);
    } finally {
      setProcessingAction(false);
    }
  };

  const getActionDescription = () => {
    const selectedGroupsList = Object.entries(selectedGroups)
      .filter(([_, selected]) => selected)
      .map(([group]) => group)
      .join(', ');
      
    if (selectedGroupsList.length === 0) {
      return "Please select at least one group";
    }
    
    let desc = `This action will affect production devices in groups: ${selectedGroupsList}`;
    
    if (useVersionFilter) {
      const selectedVersionsList = Object.entries(selectedVersions)
        .filter(([_, selected]) => selected)
        .map(([version]) => version)
        .join(', ');
        
      if (selectedVersionsList.length === 0) {
        desc += " (please select at least one version)";
      } else {
        desc += ` that ${versionFilterMode === 'include' ? 'are running' : 'are not running'} version${selectedVersionsList.includes(',') ? 's' : ''}: ${selectedVersionsList}`;
      }
    } else {
      desc += " (all versions)";
    }
    
    return desc;
  };

  // Check if deploy buttons should be disabled
  const shouldDisableActionButtons = () => {
    return processingAction || 
           !hasSelectedGroups || 
           (useVersionFilter && !Object.values(selectedVersions).some(isSelected => isSelected));
  };

  if (!isOpen) return null;

  return (
    <div className={inline ? 'card h-100' : 'modal show d-block'} tabIndex={inline ? undefined : -1} style={inline ? undefined : { backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className={inline ? '' : 'modal-dialog modal-lg'}>
        <div className={inline ? '' : 'modal-content'}>
          {inline ? (
            <div className="card-header bg-white">
              <h5 className="mb-1">Production Deployment</h5>
              <div className="text-muted small">Set production devices to opt-in or forced OTA by group and optional version filter.</div>
            </div>
          ) : (
            <div className="modal-header">
              <h5 className="modal-title">Make a Deployment</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
          )}
          <div className={inline ? 'card-body' : 'modal-body'}>
            {/* Groups Selection */}
            <div className="mb-4">
              <div className="d-flex align-items-center mb-2">
                <h6 className="mb-0 me-3">Groups:</h6>
                <button className="btn btn-sm btn-outline-primary me-2" onClick={selectAllGroups}>
                  Select All
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={clearAllGroups}>
                  Clear All
                </button>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {alphabet.map(letter => (
                  <button 
                    key={letter}
                    className={`btn ${selectedGroups[letter] ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{ width: '40px', height: '40px', padding: '0' }}
                    onClick={() => toggleGroup(letter)}
                  >
                    {letter}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Version Filter Toggle */}
            <div className="mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="versionFilterToggle"
                  checked={useVersionFilter}
                  onChange={() => setUseVersionFilter(!useVersionFilter)}
                />
                <label className="form-check-label" htmlFor="versionFilterToggle">
                  Filter by version
                </label>
              </div>
            </div>
            
            {/* Version Selection */}
            {useVersionFilter && (
              <div className="mb-4">
                <div className="d-flex align-items-center mb-2">
                  <h6 className="mb-0 me-3">Version Filter:</h6>
                  <div className="btn-group" role="group">
                    <input 
                      type="radio" 
                      className="btn-check" 
                      name="versionFilterMode" 
                      id="versionInclude" 
                      checked={versionFilterMode === 'include'} 
                      onChange={() => setVersionFilterMode('include')} 
                    />
                    <label className="btn btn-outline-primary" htmlFor="versionInclude">Include Only</label>
                    
                    <input 
                      type="radio" 
                      className="btn-check" 
                      name="versionFilterMode" 
                      id="versionExclude" 
                      checked={versionFilterMode === 'exclude'} 
                      onChange={() => setVersionFilterMode('exclude')} 
                    />
                    <label className="btn btn-outline-danger" htmlFor="versionExclude">Exclude</label>
                  </div>
                </div>
                
                <div className="d-flex mb-2">
                  <button className="btn btn-sm btn-outline-primary me-2" onClick={selectAllVersions}>
                    Select All
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={clearAllVersions}>
                    Clear All
                  </button>
                </div>
                
                {loading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading versions...</span>
                    </div>
                    <span className="ms-2">Loading versions...</span>
                  </div>
                ) : versions.length === 0 ? (
                  <div className="alert alert-warning">
                    No versions available
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {versions.map(version => (
                      <button 
                        key={version}
                        className={`btn ${selectedVersions[version] ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => toggleVersion(version)}
                      >
                        {version}
                        {selectedVersions[version] && (
                          <FontAwesomeIcon icon={faCheck} className="ms-1" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
                <small className="text-muted mt-2 d-block">
                  {versionFilterMode === 'include' 
                    ? `This will only affect devices running the selected version(s)` 
                    : `This will affect all devices except those running the selected version(s)`}
                </small>
              </div>
            )}
            
            <div className="alert alert-info">
              <small>{getActionDescription()}</small>
            </div>
          </div>
          <div className={inline ? 'card-footer bg-white d-flex justify-content-end gap-2' : 'modal-footer'}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => handleDeployment('none')}
              disabled={shouldDisableActionButtons()}
            >
              {processingAction ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              ) : (
                <FontAwesomeIcon icon={faUndo} className="me-2" />
              )}
              Clear OTA
            </button>
            <button 
              type="button" 
              className="btn btn-success" 
              onClick={() => handleDeployment('opt-in')}
              disabled={shouldDisableActionButtons()}
            >
              {processingAction ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              ) : (
                <FontAwesomeIcon icon={faRocket} className="me-2" />
              )}
              Deploy Opt-in
            </button>
            <button 
              type="button" 
              className="btn btn-danger" 
              onClick={() => handleDeployment('force')}
              disabled={shouldDisableActionButtons()}
            >
              {processingAction ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              ) : (
                <FontAwesomeIcon icon={faBolt} className="me-2" />
              )}
              Deploy Force
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeploymentModal;